import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useMediaQuery } from "@mui/material";
import {
  CarouselContainer,
  ScrollContainer,
  SlideContainer,
  NavigationButton,
  DotContainer,
  Dot,
  Slide,
  NextArrow,
  PrevArrow,
} from "./styled";
import { MOBILE_SCREEN_SIZE } from "../AppNavigation/constants";

interface CarouselProps {
  children: React.ReactNode;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showDots?: boolean;
  showArrows?: boolean;
  className?: string;
  slidesToShow?: number; // New prop for number of slides to show
  forceShowArrows?: boolean; // Add prop to force showing arrows even when auto-hiding
}

const Carousel: React.FC<CarouselProps> = ({
  children,
  autoPlay = false,
  autoPlayInterval = 3000,
  showDots = false,
  showArrows = true,
  className,
  slidesToShow = 1,
  forceShowArrows = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [leftDisabled, setLeftDisabled] = useState<boolean>(true);
  const [rightDisabled, setRightDisabled] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [dragThresholdExceeded, setDragThresholdExceeded] = useState(false);
  const [shouldShowArrows, setShouldShowArrows] = useState(false);
  const [shouldCenter, setShouldCenter] = useState(false);
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_SCREEN_SIZE}px)`);
  const startX = useRef<number>(0);
  const scrollLeft = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout>(undefined);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  const DRAG_THRESHOLD = 100;

  // Check if items should be centered
  const checkCentering = useCallback(() => {
    if (containerRef.current) {
      const hasOverflow =
        containerRef.current.scrollWidth > containerRef.current.clientWidth;
      setShouldCenter(!hasOverflow);
    }
  }, []);

  // Check if content is scrollable (needs pagination)
  const checkScrollable = useCallback(() => {
    if (containerRef.current) {
      const hasOverflow =
        containerRef.current.scrollWidth > containerRef.current.clientWidth;
      // Hide arrows on mobile devices regardless of overflow
      setShouldShowArrows(
        (hasOverflow && showArrows && !isMobile) || forceShowArrows
      );

      // Also check for centering
      checkCentering();
    }
  }, [showArrows, isMobile, forceShowArrows, checkCentering]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragThresholdExceeded(false);
    startX.current = e.pageX - (containerRef.current?.offsetLeft || 0);
    scrollLeft.current = containerRef.current?.scrollLeft || 0;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    const x = e.pageX - (containerRef.current.offsetLeft || 0);
    const walk = (x - startX.current) * 1;

    if (Math.abs(walk) > DRAG_THRESHOLD) {
      setDragThresholdExceeded(true);
    }

    containerRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragThresholdExceeded(false);
  }, []);

  const totalSlides = useMemo(() => {
    if (windowWidth <= 768) {
      return Math.ceil(React.Children.count(children) / (slidesToShow / 2));
    }
    return Math.ceil(React.Children.count(children) / slidesToShow);
  }, [children, slidesToShow, windowWidth]);

  const scroll = (direction: "left" | "right"): void => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const scrollAmount = container.clientWidth;
    const maxScroll = container.scrollWidth - container.clientWidth;

    if (direction === "left") {
      setRightDisabled(false);
      setLeftDisabled(container.scrollLeft - scrollAmount <= 0);
    } else {
      setLeftDisabled(false);
      setRightDisabled(container.scrollLeft + scrollAmount >= maxScroll);
    }

    let newScrollLeft =
      direction === "left"
        ? Math.max(container.scrollLeft - scrollAmount, 0)
        : Math.min(container.scrollLeft + scrollAmount, maxScroll);

    container.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });
  };

  const startAutoPlay = (): void => {
    if (autoPlay && totalSlides > 1) {
      autoPlayRef.current = setInterval(() => {
        if (containerRef.current) {
          const container = containerRef.current;
          const maxScroll = container.scrollWidth - container.clientWidth;
          const newScrollLeft = container.scrollLeft + container.clientWidth;

          if (newScrollLeft > maxScroll) {
            container.scrollTo({ left: 0, behavior: "smooth" });
          } else {
            scroll("right");
          }
        }
      }, autoPlayInterval);
    }
  };

  const stopAutoPlay = (): void => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  // Update arrow visibility and centering when content or window size changes
  useEffect(() => {
    checkScrollable();
  }, [
    children,
    windowWidth,
    showArrows,
    forceShowArrows,
    isMobile,
    checkScrollable,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (container) {
      container.addEventListener("mouseleave", handleMouseUp);
      container.addEventListener("mouseup", handleMouseUp);
    }

    const handleScroll = (): void => {
      const index = Math.round(container.scrollLeft / container.clientWidth);
      if (container.scrollLeft === 0) {
        setLeftDisabled(true);
      } else {
        setLeftDisabled(false);
      }
      if (
        container.scrollLeft ===
        container.scrollWidth - container.clientWidth
      ) {
        setRightDisabled(true);
      } else {
        setRightDisabled(false);
      }
      setCurrentIndex(index);
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      checkScrollable();
    };

    container.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
        container.removeEventListener("mouseleave", handleMouseUp);
        container.removeEventListener("mouseup", handleMouseUp);
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [handleMouseUp, checkScrollable]);

  useEffect(() => {
    startAutoPlay();
    return () => stopAutoPlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, autoPlayInterval, children]);

  // Initialize slideRefs array
  useEffect(() => {
    slideRefs.current = slideRefs.current.slice(
      0,
      React.Children.count(children)
    );
  }, [children]);

  return (
    <CarouselContainer className={className}>
      {shouldShowArrows && !isMobile && (
        <>
          <NavigationButton
            sx={{ left: "-2%" }}
            onClick={() => scroll("left")}
            size="small"
            aria-label="Previous slide"
            disabled={leftDisabled}
          >
            <PrevArrow />
          </NavigationButton>

          <NavigationButton
            sx={{ right: "-2%" }}
            onClick={() => scroll("right")}
            size="small"
            aria-label="Next slide"
            disabled={rightDisabled}
          >
            <NextArrow />
          </NavigationButton>
        </>
      )}

      <ScrollContainer
        ref={containerRef}
        aria-label="Carousel"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <SlideContainer
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${React.Children.count(
              children
            )}, calc(${100 / slidesToShow}% - 20px))`,
            gap: "20px",
            justifyContent: shouldCenter ? "center" : "start",
            [`@media screen and (max-width: 768px)`]: {
              gridTemplateColumns: `repeat(${React.Children.count(children)}, ${200 / slidesToShow
                }%)`,
              gap: 0,
            },
          }}
        >
          {React.Children.map(children, (child, index) => (
            <Slide
              key={index}
              aria-label={`Slide ${index + 1}`}
              style={{
                pointerEvents: dragThresholdExceeded ? "none" : "all",
              }}
              ref={(el) => {
                slideRefs.current[index] = el as HTMLDivElement | null;
              }}
            >
              {child}
            </Slide>
          ))}
        </SlideContainer>
      </ScrollContainer>

      {showDots && (
        <DotContainer>
          {Array.from({ length: totalSlides }, (_, index) => (
            <Dot
              key={index}
              active={currentIndex === index}
              onClick={() => {
                containerRef.current?.scrollTo({
                  left: index * (containerRef.current?.clientWidth || 0),
                  behavior: "smooth",
                });
              }}
              aria-label={`Go to slide group ${index + 1}`}
            />
          ))}
        </DotContainer>
      )}
    </CarouselContainer>
  );
};

export default Carousel;
